import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaSearch,
    FaBookOpen,
    FaClock,
    FaSignal,
    FaRocket,
    FaArrowRight,
    FaStar,
    FaChalkboardTeacher
} from 'react-icons/fa';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './CourseCatalog.css';

const CourseCatalog = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const openCourseDetail = (courseId) => {
        navigate(`/course-detail/${courseId}`);
    };


    const fetchCatalog = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/student/catalog', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCourses(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching catalog", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCatalog();
    }, []);

    const filteredCourses = courses.filter(c => {
        const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCat = selectedCategory === 'All' || c.category === selectedCategory;
        return matchesSearch && matchesCat;
    });

    const categoryCounts = courses.reduce((acc, c) => {
        if (c.category) {
            acc[c.category] = (acc[c.category] || 0) + 1;
        }
        return acc;
    }, {});

    const sortedCats = Object.keys(categoryCounts).sort((a, b) => categoryCounts[b] - categoryCounts[a]);
    const top4 = sortedCats.slice(0, 4);
    const categories = ['All', ...top4];

    if (loading) return <Loader />;

    return (
        <div className="course-catalog-v2 fade-in">
            {/* --- Premium Hero Section --- */}
            <div className="catalog-hero">
                <div className="hero-content">
                    <div className="hero-label"><FaRocket /> Elevate Your Career</div>
                    <h1 className="hero-title">Master the Skills of <span className="highlight-text">Tomorrow</span></h1>
                    <p className="hero-description">
                        Access industry-vetted curricula designed by elite professionals.
                        Enroll today to unlock premium job opportunities and lifetime mentorship.
                    </p>

                    <div className="hero-search-container">
                        <div className="hero-search-bar">
                            <input
                                type="text"
                                placeholder="What skill do you want to master today?"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button className="search-btn">Find Courses</button>
                        </div>
                        <div className="trending-tags">
                            <span>Trending:</span>
                            <button onClick={() => setSearchTerm('Full Stack')}>Full Stack</button>
                            <button onClick={() => setSearchTerm('Data Science')}>Data Science</button>
                            <button onClick={() => setSearchTerm('UI/UX')}>UI/UX</button>
                        </div>
                    </div>
                </div>
                <div className="hero-stats-overlay">
                    <div className="h-stat-card">
                        <div className="h-stat-icon"><FaBookOpen /></div>
                        <div>
                            <h4>500+</h4>
                            <p>Global Courses</p>
                        </div>
                    </div>
                    <div className="h-stat-card">
                        <div className="h-stat-icon gold"><FaChalkboardTeacher /></div>
                        <div>
                            <h4>120+</h4>
                            <p>Expert Tutors</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Filtering & Grid Section --- */}
            <div className="catalog-main">
                <div className="catalog-controls">
                    <div className="category-tabs">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`cat-tab ${selectedCategory === cat ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                    <div className="results-count">
                        Showing <strong>{filteredCourses.length}</strong> premium results
                    </div>
                </div>

                <div className="browse-table-wrapper">
                    <table className="browse-table">
                        <thead>
                            <tr>
                                <th>Course Details</th>
                                <th>Category</th>
                                <th>Level</th>
                                <th>Instructor</th>
                                <th>Price</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCourses.map(course => (
                                <tr key={course._id} onClick={() => openCourseDetail(course._id)} className="browse-tr">
                                    <td className="bt-col-info">
                                        <div className="bt-info-wrap">
                                            <div className="bt-thumb">
                                                {course.thumbnailUrl
                                                    ? <img src={`${API_URL}${course.thumbnailUrl}`} alt={course.title} />
                                                    : <div className="bt-thumb-ph"><FaBookOpen /></div>}
                                            </div>
                                            <div className="bt-title-wrap">
                                                <h3>{course.title}</h3>
                                                <span className="bt-id">ID: {course._id.slice(-6).toUpperCase()}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="bt-category">{course.category}</span></td>
                                    <td><span className={`bt-level bt-level-${(course.difficultyLevel || 'Beginner').toLowerCase()}`}>{course.difficultyLevel}</span></td>
                                    <td className="bt-tutor">{course.tutorId?.name || 'Expert Instructor'}</td>
                                    <td className="bt-price">
                                        {course.price === 0 ? <span className="bt-free">Free</span> : `$${course.price}`}
                                    </td>
                                    <td className="bt-action">
                                        <button className="bt-apply-btn" onClick={(e) => { e.stopPropagation(); openCourseDetail(course._id); }} style={{ borderRadius: 0 }}>
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredCourses.length === 0 && (
                    <div className="empty-catalog">
                        <FaSearch size={48} />
                        <h3>No matches found</h3>
                        <p>We couldn't find any courses matching your specific criteria. Try broadening your keywords or filters.</p>
                        <button onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }} className="reset-btn">Reset All Filters</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseCatalog;
